import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import axiosInstance from '../../api/axiosInstance';
import { useAuth } from '../../contexts/AuthContext';
import { Select, Textarea, Button } from '../ui';
import { FormField } from '../ui/Form';
import { getFieldIcon } from '../../utils/formIcons';
import { ArrowUturnLeftIcon, MagnifyingGlassIcon, HashtagIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import Resultados from './Resultados';

interface Resultado {
  orderId: string;
  error?: string;
}

const BodegaDevolucion: React.FC = () => {
  const [orderIds, setOrderIds] = useState<string>('');
  const [exitosos, setExitosos] = useState<string[]>([]);
  const [fallidos, setFallidos] = useState<Resultado[]>([]);
  const [searchType, setSearchType] = useState<'tracking' | 'id'>('tracking');
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Convertir el texto en un array de IDs, soportando tanto comas como saltos de línea
    const ids = orderIds
      .split(/[\n,]+/)
      .map(id => id.trim())
      .filter(id => id !== '');

    if (ids.length === 0) {
      toast.error('Por favor ingrese al menos un número de guía o ID de pedido', { position: 'top-center' });
      return;
    }

    setIsLoading(true);
    try {
      const requestBody = {
        userId: user?.id,
        orders: ids.map(id => {
          if (searchType === 'tracking') {
            return {
              trackingNumber: id,
              searchType: "tracking",
              status: "bodega-devolucion"
            };
          } else {
            return {
              orderId: id,
              searchType: "id",
              status: "bodega-devolucion"
            };
          }
        })
      };
      
      console.log('Datos enviados al servidor:', JSON.stringify(requestBody, null, 2));

      const response = await axiosInstance.patch('/status/update', requestBody);

      const data = response.data;
      console.log('Respuesta del servidor:', data);
      
      // Transformar los resultados exitosos
      const exitososTransformados = Array.isArray(data.results?.success) 
        ? data.results.success.map((item: any) => {
            if (typeof item === 'object' && item !== null) {
              return String(item[searchType === 'tracking' ? 'trackingNumber' : 'orderId'] || '');
            }
            return String(item);
          })
        : [];

      // Transformar los resultados fallidos
      const fallidosTransformados = Array.isArray(data.results?.failed)
        ? data.results.failed.map((item: any) => ({
            orderId: String(item[searchType === 'tracking' ? 'trackingNumber' : 'orderId'] || ''),
            error: String(item.error || 'Error desconocido')
          }))
        : [];

      setExitosos(exitososTransformados);
      setFallidos(fallidosTransformados);

      if (fallidosTransformados.length > 0) {
        toast.error('Algunos pedidos no se pudieron actualizar', { position: 'top-center' });
      } else {
        toast.success('Estados actualizados correctamente', { position: 'top-center' });
      }
      
      setOrderIds('');
    } catch (error) {
      toast.error('Error al actualizar los estados', { position: 'top-center' });
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-center mb-2">
            <ArrowUturnLeftIcon className="h-7 w-7 text-slate-600 mr-3" />
            <h2>Cambiar estado a bodega devolución</h2>
          </div>
          <p className="text-center text-gray-600 text-sm">
            Actualiza el estado de pedidos a "Bodega devolución" usando números de guía o IDs de pedido
          </p>
        </div>

        {/* Formulario */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField 
              label="Tipo de búsqueda" 
              icon={MagnifyingGlassIcon}
            >
              <Select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value as 'tracking' | 'id')}
                options={[
                  { value: 'tracking', label: 'Número de guía' },
                  { value: 'id', label: 'ID de pedido' }
                ]}
              />
            </FormField>

            <FormField 
              label={searchType === 'tracking' ? 'Números de guía' : 'IDs de pedidos'} 
              helpText="Separe múltiples valores con comas o saltos de línea"
              icon={searchType === 'tracking' ? getFieldIcon('guia') : HashtagIcon}
            >
              <Textarea
                value={orderIds}
                onChange={(e) => setOrderIds(e.target.value)}
                rows={8}
                placeholder={searchType === 'tracking' ? "ABC123\nABC124\nABC125" : "4823\n4822\n4821"}
              />
            </FormField>

            <Button
              type="submit"
              variant="primary"
              isLoading={isLoading}
              leftIcon={CheckCircleIcon}
              fullWidth
              size="lg"
            >
              {isLoading ? 'Actualizando...' : 'Actualizar estados'}
            </Button>
          </form>
        </div>

        {/* Resultados */}
        <Resultados exitosos={exitosos} fallidos={fallidos} />
      </div>
    </div>
  );
};

export default BodegaDevolucion;
